import {
  getCurrentEnvironmentConfig,
  getSelectedEnvironment,
} from '@choco-admin/utils';
import { getUrl } from 'aws-amplify/storage';
import { useCallback, useRef, useState } from 'react';

import {
  libraryBucketName,
  libraryObjectPath,
  readLibraryResponse,
  type LibraryZone,
} from '../utils/deliveryZoneLibrary';

/** Why the list is not showing, in terms the UI can explain. */
export type LibraryStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'failed';

interface UseDeliveryZoneLibraryResult {
  zones: LibraryZone[];
  status: LibraryStatus;
  /** Fetch a country's file, or serve it from this session's cache. */
  loadCountry: (locale: string) => Promise<void>;
}

/**
 * The predefined delivery areas for one country.
 *
 * Files are fetched on demand and cached per locale for the session: a country
 * file covers a whole market and is large enough that re-fetching it every time
 * the picker opens would be wasteful.
 *
 * A missing file is a normal outcome, not a failure. Not every locale has one
 * uploaded in every environment, so that case reports `empty` and the UI says so
 * rather than showing an error.
 */
export const useDeliveryZoneLibrary = (): UseDeliveryZoneLibraryResult => {
  const [zones, setZones] = useState<LibraryZone[]>([]);
  const [status, setStatus] = useState<LibraryStatus>('idle');
  const cacheRef = useRef<Map<string, LibraryZone[]>>(new Map());
  // Only the newest request may write results: switching country twice quickly
  // must not let the first file land after the second.
  const requestIdRef = useRef(0);

  const loadCountry = useCallback(async (locale: string) => {
    // Bump first, and for cached hits too. Switching to a country that is still
    // loading and then back to a cached one must invalidate the in-flight
    // request, otherwise its late response lands afterwards and replaces the
    // cached country's areas with another country's, which then save under the
    // wrong distributor.
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    const cached = cacheRef.current.get(locale);
    if (cached) {
      setZones(cached);
      setStatus(cached.length > 0 ? 'ready' : 'empty');
      return;
    }

    setStatus('loading');

    try {
      const environmentKey = getSelectedEnvironment();
      const { region } = getCurrentEnvironmentConfig();

      // Path-based input on purpose: the key-based API prefixes an access level
      // onto the object name, and these files sit at the bucket root.
      const { url } = await getUrl({
        path: libraryObjectPath(locale),
        options: {
          bucket: { bucketName: libraryBucketName(environmentKey), region },
          expiresIn: 300,
        },
      });

      const response = await fetch(url.toString());
      if (requestId !== requestIdRef.current) return;

      if (response.status === 404 || response.status === 403) {
        // 403 is what S3 returns for a missing object when listing is denied,
        // so it means the same thing as 404 here.
        cacheRef.current.set(locale, []);
        setZones([]);
        setStatus('empty');
        return;
      }

      if (!response.ok) throw new Error(`Library request failed`);

      const parsed = await readLibraryResponse(response);
      if (requestId !== requestIdRef.current) return;

      cacheRef.current.set(locale, parsed);
      setZones(parsed);
      setStatus(parsed.length > 0 ? 'ready' : 'empty');
    } catch {
      if (requestId !== requestIdRef.current) return;
      setZones([]);
      setStatus('failed');
    }
  }, []);

  return { zones, status, loadCountry };
};
